import { useState } from 'react'
import { Alert, Button, Table } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { formatDate, formatDateTime } from '@/shared/lib/format'
import { PageHeader } from '@/shared/ui/PageHeader'
import { PurchaseReceiveModal } from '@/features/supplier/PurchaseReceiveModal'
import { PurchaseStateTag } from '@/features/supplier/PurchaseTags'
import { fetchReceivingQueue, type Receiving } from '@/features/supplier/supplierApi'

export function PurchaseReceivingPage() {
  const { t } = useTranslation()
  const [receiving, setReceiving] = useState<Receiving | null>(null)
  const queue = useQuery({ queryKey: queryKeys.supplier.receiving, queryFn: ({ signal }) => fetchReceivingQueue(signal) })

  return (
    <>
      <PageHeader title={t('nav.purchaseReceiving')} description={t('pageIntro.purchaseReceiving')} />
      {queue.isError ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(queue.error, t)} /> : null}
      <Table<Receiving>
        rowKey="id"
        loading={queue.isPending}
        dataSource={queue.data ?? []}
        pagination={false}
        scroll={{ x: 1100 }}
        locale={{ emptyText: t('purchase.receive.queueEmpty') }}
        expandable={{
          expandedRowRender: (row) => (
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={row.lines}
              columns={[
                { key: 'description', title: t('purchase.field.description'), render: (_, line) => line.description },
                { key: 'ordered', title: t('purchase.receive.ordered'), align: 'right', render: (_, line) => line.quantity },
                { key: 'received', title: t('purchase.receive.received'), align: 'right', render: (_, line) => line.receivedQuantity },
                { key: 'remaining', title: t('purchase.receive.remaining'), align: 'right', render: (_, line) => line.remainingQuantity },
              ]}
            />
          ),
        }}
        columns={[
          { key: 'number', title: t('purchase.field.number'), width: 120, render: (_, row) => row.number },
          { key: 'supplier', title: t('purchase.field.supplier'), width: 220, render: (_, row) => row.supplierName },
          { key: 'invoice', title: t('purchase.field.invoiceNo'), width: 140, render: (_, row) => row.invoiceNo ?? t('common.dash') },
          { key: 'invoiceDate', title: t('purchase.field.invoiceDate'), width: 120, render: (_, row) => formatDate(row.invoiceDate) },
          { key: 'warehouse', title: t('purchase.field.warehouse'), width: 180, render: (_, row) => row.warehouse.name },
          { key: 'state', title: t('purchase.field.state'), width: 150, render: (_, row) => <PurchaseStateTag state={row.state} /> },
          {
            key: 'verified',
            title: t('purchase.field.verifiedBy'),
            width: 220,
            render: (_, row) => (row.verifiedBy ? `${row.verifiedBy.name} · ${formatDateTime(row.verifiedAt)}` : t('common.dash')),
          },
          {
            key: 'actions',
            title: t('common.actions'),
            width: 140,
            fixed: 'right',
            render: (_, row) => (
              <Button type="primary" size="small" icon={<InboxOutlined />} onClick={() => setReceiving(row)}>
                {t('purchase.receive.open')}
              </Button>
            ),
          },
        ]}
      />
      <PurchaseReceiveModal receiving={receiving} onClose={() => setReceiving(null)} />
    </>
  )
}
